import { useCallback, useEffect, useState } from 'react';
import {
  Banknote, CheckCircle2, Download, Loader2, XCircle,
} from 'lucide-react';
import { adminApi } from '../services/api';
import {
  annoncerErreur, annoncerSucces, Bandeau, demanderMotif, EtatErreur, EtatSansDonnees, SqueletteTableau,
} from '../etats';
import EtatVide from './EtatVide';

/* ═══════════════════════════════════════════════════════════════════════
   RETRAITS À TRAITER

   Trois gestes, trois moments, et les confondre serait refaire le défaut
   que la séparation validateur / payeur corrige ailleurs :

       demander  →  autoriser  →  verser

   Celui qui autorise ne verse pas. L'écran le dit AVANT le clic en
   désactivant le bouton pour lui : découvrir une règle par un message
   d'erreur, c'est l'apprendre par l'échec.

   La facture paraît à l'autorisation, et sur la base du retrait — elle
   cite les relevés qu'elle solde, pour qu'un comptable les retrouve.
   ═══════════════════════════════════════════════════════════════════════ */

const ETATS = {
  demandee: { libelle: 'En attente', couleur: 'var(--attention)' },
  autorisee: { libelle: 'Autorisée', couleur: 'var(--accent-texte)' },
  payee: { libelle: 'Versée', couleur: 'var(--succes)' },
  refusee: { libelle: 'Refusée', couleur: 'var(--alerte)' },
};

const francs = (v) => `${Number(v || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} FCFA`;
const jour = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR');
};
const aujourdHui = () => new Date().toISOString().slice(0, 10);

function Versement({ retrait, moi, onFait }) {
  const [ouvert, setOuvert] = useState(false);
  const [mode, setMode] = useState('virement');
  const [date, setDate] = useState(aujourdHui());
  const [reference, setReference] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  // Le nom plutôt que l'identifiant : c'est ce que la personne reconnaît.
  const jAiAutorise = retrait.autorisee_par && retrait.autorisee_par === moi;

  if (retrait.statut === 'payee') {
    return (
      <span className="text-[12.5px] text-[var(--text-muted)]">
        Versé le {jour(retrait.paye_le)}
        {retrait.paye_par ? ` par ${retrait.paye_par}` : ''}
        {retrait.mode ? ` (${retrait.mode})` : ''}
      </span>
    );
  }
  if (jAiAutorise) {
    return (
      <span className="text-[12.5px]" style={{ color: 'var(--attention)' }}>
        Vous avez autorisé ce retrait : un autre responsable doit le verser.
      </span>
    );
  }
  if (!ouvert) {
    return (
      <button type="button" className="act act-contour" onClick={() => setOuvert(true)}>
        <Banknote className="w-4 h-4" />
        Enregistrer le versement
      </button>
    );
  }

  const verser = async () => {
    setEnvoi(true); setErreur('');
    try {
      await adminApi.traiterUnRetrait(retrait.id, 'payer',
        { mode, date_paiement: date, reference });
      annoncerSucces(`Versement de ${francs(retrait.montant)} enregistré pour ${retrait.intervenant}.`);
      onFait();
    } catch (e) {
      setErreur(e.message);
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="text-left space-y-2 min-w-[240px]">
      {erreur && <div role="alert" className="encart encart-alerte">{erreur}</div>}
      <div className="flex gap-2 flex-wrap">
        <select value={mode} onChange={(e) => setMode(e.target.value)}
                className="champ" aria-label="Mode de versement">
          <option value="virement">Virement bancaire</option>
          <option value="especes">Espèces</option>
          <option value="mobile">Mobile money</option>
        </select>
        {/* Ni future, ni antérieure à l'autorisation : le serveur refuse
            les deux, et pour la même raison. */}
        <input type="date" value={date} max={aujourdHui()}
               onChange={(e) => setDate(e.target.value)}
               className="champ" aria-label="Date du versement" />
      </div>
      <input value={reference} onChange={(e) => setReference(e.target.value)}
             placeholder="Référence (n° de virement, reçu…)" className="champ" />
      <div className="flex gap-2">
        <button type="button" className="act" disabled={envoi} aria-busy={envoi || undefined} onClick={verser}>
          {envoi ? <Loader2 className="w-4 h-4 animate-spin" />
            : <CheckCircle2 className="w-4 h-4" />}
          Confirmer {francs(retrait.montant)}
        </button>
        <button type="button" className="act act-tertiaire" onClick={() => setOuvert(false)}>
          Annuler
        </button>
      </div>
    </div>
  );
}

export default function RetraitsATraiter({ user }) {
  const [etat, setEtat] = useState(null);
  // L'objet erreur du chargement : son statut choisit l'écran.
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState('');

  const moi = [user?.first_name, user?.last_name].filter(Boolean).join(' ')
              || user?.username || '';

  const charger = useCallback(async () => {
    try {
      setEtat(await adminApi.retraitsATraiter());
      setErreur(null);
    } catch (e) {
      setErreur(e);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const agir = async (retrait, action) => {
    setEnCours(retrait.id);
    try {
      if (action === 'refuser') {
        const motif = await demanderMotif({
          titre: `Refuser le retrait ${retrait.numero} ?`,
          message: `${retrait.intervenant} sera prévenu. Dites pourquoi — il ne le devinera pas.`,
          confirmer: 'Refuser', placeholder: 'Ex. : relevé contesté, montant à vérifier…',
        });
        if (!motif) { setEnCours(''); return; }
        await adminApi.traiterUnRetrait(retrait.id, 'refuser', { motif: motif.trim() });
        annoncerSucces(`Retrait ${retrait.numero} refusé. L'intervenant est prévenu.`);
      } else {
        await adminApi.traiterUnRetrait(retrait.id, 'autoriser');
        annoncerSucces(`Retrait ${retrait.numero} autorisé : la facture est émise.`);
      }
      await charger();
    } catch (e) {
      annoncerErreur(e.message || "L'opération n'a pas abouti.");
    } finally {
      setEnCours('');
    }
  };

  const voirLaFacture = async (retrait) => {
    try {
      const url = URL.createObjectURL(await adminApi.factureRetrait(retrait.id));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      annoncerErreur(e.message || "La facture n'a pas pu être ouverte.");
    }
  };

  if (etat === null) {
    if (erreur?.statut === 404) {
      return (
        <EtatSansDonnees cas="interdit"
                         aide="Les retraits d'intervenants ne relèvent pas de votre accès." />
      );
    }
    return erreur
      ? <EtatErreur erreur={erreur} onReessayer={charger} />
      : <SqueletteTableau colonnes={6} />;
  }

  const attente = etat.retraits.filter((r) => r.statut === 'demandee').length;

  return (
    <div className="space-y-4">
      {erreur && (
        <Bandeau ton="attention" action={<button type="button" className="act act-sm act-contour" onClick={charger}>Réessayer</button>}>
          L’actualisation a échoué : ces données sont peut-être anciennes.
        </Bandeau>
      )}

      {etat.retraits.length === 0 ? (
        <div className="rounded-carte bg-[var(--bg-card)] shadow-carte">
          <EtatVide
            icone={Banknote}
            titre="Aucune demande de retrait"
            aide="Un intervenant demande depuis son espace, sur le solde de ses relevés validés."
          />
        </div>
      ) : (
        <div className="carte-tableau">
          <div className="p-5 pb-0">
            <h3 className="titre-carte">Demandes de retrait</h3>
            <p className="text-[12.5px] text-[var(--text-muted)] mt-1">
              {attente} en attente d’autorisation sur {etat.retraits.length}.
            </p>
          </div>
          <div className="tableau-hote">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr>
                  <th scope="col">Référence</th>
                  <th scope="col">Intervenant</th>
                  <th scope="col" className="text-right">Montant</th>
                  <th scope="col">Relevés soldés</th>
                  <th scope="col">État</th>
                  <th scope="col" className="text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {etat.retraits.map((r) => {
                  const { libelle, couleur } = ETATS[r.statut] || ETATS.demandee;
                  return (
                    <tr key={r.id}>
                      <th scope="row" className="font-normal">
                        <span className="text-[13.5px] font-medium text-[var(--text-bold)]">
                          {r.numero}
                        </span>
                        <span className="block text-[11.5px] text-[var(--text-muted)]">
                          demandé le {jour(r.demande_le)}
                        </span>
                        {['autorisee', 'payee'].includes(r.statut) && (
                          <button type="button" onClick={() => voirLaFacture(r)}
                                  className="block text-[11.5px] text-[var(--accent-texte)]
                                             hover:underline">
                            <Download className="w-3 h-3 inline mr-1" />facture
                          </button>
                        )}
                      </th>
                      <td className="text-[13px]">
                        {r.intervenant}
                        {r.message && (
                          <span className="block text-[11.5px] text-[var(--text-muted)]">
                            « {r.message} »
                          </span>
                        )}
                      </td>
                      <td className="text-right text-[14px] font-semibold">
                        {francs(r.montant)}
                      </td>
                      <td className="text-[12px] text-[var(--text-muted)]">
                        {r.releves.map((l) => l.numero).join(', ') || '—'}
                      </td>
                      <td>
                        <span className="text-[12px] font-semibold" style={{ color: couleur }}>
                          {libelle}
                        </span>
                        {r.motif_refus && (
                          <span className="block text-[11px] text-[var(--text-muted)]">
                            {r.motif_refus}
                          </span>
                        )}
                        {r.autorisee_par && r.statut !== 'refusee' && (
                          <span className="block text-[11px] text-[var(--text-muted)]">
                            par {r.autorisee_par}
                          </span>
                        )}
                      </td>
                      <td className="text-right">
                        {r.statut === 'demandee' && etat.peut_autoriser && (
                          <>
                            <button type="button" className="act act-contour"
                                    disabled={enCours === r.id} aria-busy={enCours === r.id || undefined}
                                    onClick={() => agir(r, 'autoriser')}>
                              {enCours === r.id
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <CheckCircle2 className="w-4 h-4" />}
                              Autoriser
                            </button>
                            <button type="button" className="act act-tertiaire ml-1"
                                    disabled={enCours === r.id}
                                    onClick={() => agir(r, 'refuser')} title="Refuser">
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {r.statut === 'autorisee' && etat.peut_payer && (
                          <Versement retrait={r} moi={moi} onFait={charger} />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-[12px] text-[var(--text-muted)] leading-relaxed">
        <strong>Qui autorise ne verse pas.</strong> La facture paraît à
        l’autorisation, sur la base du retrait, et cite les relevés qu’elle
        solde. Le versement lui-même se fait hors plateforme ; on l’enregistre
        ici.
      </p>
    </div>
  );
}
